<template>
  <section
    :style="heroStyle"
    class="hero"
    :aria-labelledby="`hero-title-${uniqueId}`"
  >
    <div class="hero__layer" />
    <video
      autoplay
      muted
      loop
      playsInline
      class="hero__video"
      v-if="hasVideo"
      aria-describedby="video-description"
      :poster="videoPoster"
    >
      <source :src="video" type="video/mp4" />
      Your browser does not support the video tag.
    </video>
    <p id="video-description" class="hero__videoDescription" v-if="hasVideo">
      {{ videoDescription }}
    </p>
    <div :style="containerStyle" class="hero__container">
      <c-title
        :color="titleColor"
        :size="titleSize"
        :weight="titleWeight"
        :align="align"
        :id="`hero-title-${uniqueId}`"
      >
        <slot name="title" />
      </c-title>

      <c-text
        :color="textColor"
        :size="textSize"
        :weight="textWeight"
        :align="align"
      >
        <slot name="description" />
      </c-text>

      <div :class="['hero__buttons', `hero__buttons--${align}`]">
        <slot name="buttons" />
      </div>
    </div>
  </section>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'

import CTitle from '../../00_v2/title/title.vue'
import CText from '../../00_v2/text/text.vue'

export default {
  name: 'c-hero',
  components: { CTitle, CText },
  props: {
    mobileBackgroundImage: {
      type: String,
    },
    tabletBackgroundImage: {
      type: String,
    },
    desktopBackgroundImage: {
      type: String,
    },
    height: {
      type: String,
      default: '100',
      validator: (value) =>
        ['unset', '50', '75', '100', 50, 75, 100].includes(value),
    },
    titleSize: {
      type: String,
      default: 'h1',
    },
    titleWeight: {
      type: String,
      default: '800',
    },
    titleColor: {
      type: String,
      default: '#FFFFFF',
    },
    textSize: {
      type: String,
      default: 'sm',
    },
    textWeight: {
      type: String,
      default: '400',
    },
    textColor: {
      type: String,
      default: '#FFFFFF',
    },
    align: {
      type: String,
      default: 'center',
    },
    hasVideo: {
      type: Boolean,
      default: false,
    },
    video: {
      type: String,
    },
    videoDescription: {
      type: String,
    },
    videoPoster: {
      type: String
    }
  },
  data() {
    return {
      isMobile: window.innerWidth <= 480,
      isTablet: window.innerWidth > 480 && window.innerWidth <= 1024,
      hasCarouselAbove: false,
      uniqueId: uuidv4(),
    }
  },
  computed: {
    heroStyle() {
      let backgroundImage

      if (this.hasVideo) {
        return {
          height: `${this.height}vh`,
        }
      }

      if (this.isMobile) {
        backgroundImage = this.mobileBackgroundImage
      } else if (this.isTablet) {
        backgroundImage = this.tabletBackgroundImage
      } else {
        backgroundImage = this.desktopBackgroundImage
      }

      return {
        backgroundImage: `url(${backgroundImage})`,
        height: `${this.height}vh`,
      }
    },
    containerStyle() {
      return {
        width: this.hasCarouselAbove ? '80%' : '100%',
      }
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.checkForCarouselAbove()
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth <= 480
      this.isTablet = window.innerWidth > 480 && window.innerWidth <= 1024
    },
    checkForCarouselAbove() {
      const { parentElement } = this.$el
      if (
        parentElement &&
        parentElement.classList.contains('carousel__container')
      ) {
        this.hasCarouselAbove = true
      } else {
        this.hasCarouselAbove = false
      }
    },
    titleContent() {
      const slot = this.$slots.title
      if (slot) {
        const slotContent = slot()
        if (slotContent.length > 0 && slotContent[0].children) {
          return slotContent[0].children
        }
      }
      return ''
    },
  },
}
</script>

<style lang="scss" scoped>
.hero {
  align-items: center;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  color: white;
  display: flex;
  padding: 3.938rem 2.1875rem;
  position: relative;
  justify-content: center;
  width: 100%;
  overflow: hidden;

  @media screen and (min-width: 992px) {
    padding: 10.313rem 6.5rem;
  }

  &__layer {
    height: 100%;
    width: 100%;
    background: linear-gradient(
        180deg,
        rgba(59, 59, 59, 0.9) 0,
        rgba(59, 59, 59, 0.8) 10.15%,
        rgba(59, 59, 59, 0.3) 19.3%,
        rgba(59, 59, 59, 0) 61.46%,
        #3b3b3b 100%
      ),
      linear-gradient(180deg, rgba(59, 59, 59, 0.3) 0, #3b3b3b 100%);
    position: absolute;
    z-index: 1;
    opacity: 0.6;
  }

  &__video {
    position: absolute;
    z-index: 0;
    width: 100%;
    height: 100vh;
    object-fit: cover;
  }

  &__videoDescription {
    display: none;
  }

  &__container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    z-index: 1;
  }

  &__buttons {
    display: flex;
    gap: 1rem;
    margin-top: 0.5rem;
    flex-direction: column;

    @media screen and (min-width: 768px) {
      flex-direction: row;
    }
  }
}
</style>
